/**
 * Analytics event name constants.
 *
 * This module provides a type-safe way to reference analytics event names
 * throughout the application. The event names are defined in AHOY_EVENT_NAMES
 * in the Ruby backend and manually mapped here.
 *
 * Keys match the GraphQL enum names (UPPERCASE) for consistency.
 * Values are the actual event names sent to analytics services (PostHog, Google Analytics, etc.)
 *
 * @example
 * import { ANALYTICS_EVENT_NAMES } from '@love-irish/analytics/events'
 *
 * analytics.track(ANALYTICS_EVENT_NAMES.MINIGAME_STARTED, { game_id: 123 })
 */

/**
 * Flat mapping of event names.
 * Keep this in sync with AHOY_EVENT_NAMES in config/initializers/ahoy.rb
 */
export const ANALYTICS_EVENT_NAMES = {
  // Minigame events
  MINIGAME_STARTED: "minigame.started",
  MINIGAME_ATTEMPT: "minigame.attempt",
  MINIGAME_COMPLETED: "minigame.completed",
  MINIGAME_PLAY_AGAIN: "minigame.play_again",
  MINIGAME_VIEW: "$view",

  // Story events
  STORY_STARTED: "story.started",
  STORY_COMPLETED: "story.completed",

  // Codex events
  CODEX_SEARCH_SUBMITTED: "codex.search.submitted",
  CODEX_SEARCH_VIEWED: "codex.search.viewed",

  // Feedback events
  FEEDBACK_SUBMITTED: "feedback.submitted",

  // Newsletter events
  NEWSLETTER_SIGNUP: "newsletter.signup",

  // Modal events (generic — use the `name` property to identify which modal, e.g. "profile_customize")
  MODAL_OPENED: "modal.opened",
  MODAL_SUBMITTED: "modal.submitted",
  // Fired when a paged modal shows a new page (carries `page` + `section`), so we can see how far
  // through a multi-page reference users read.
  MODAL_PAGE_VIEWED: "modal.page_viewed",

  // Page events
  PAGE_VIEW: "$view",
  PAGEVIEW: "$pageview",

  // Navigation events
  LINK_CLICKED: "clicked#link",
  FORM_SUBMITTED: "form#submit",

  // Feature upsell events
  FEATURE_UPSELL_VIEWED: "feature.upsell.viewed",
  FEATURE_NO_ACCESS_VIEWED: "feature.no_access.viewed",

  // Typically fired for feature modals like "Add recording" than inline since a page might contain a large number.
  FEATURE_WAITLIST_VIEWED: "feature.waitlist.viewed",

  // FeatureRail painted-door demand signal (feature key + placement in props)
  FEATURE_INTEREST: "feature.interest",

  // /tv ambient daily-scene view (beginner "language in the living room" MVP)
  TV_SCENE_VIEWED: "tv.scene.viewed",

  // Core Web Vitals + supporting field metrics (LCP/INP/CLS/FCP/TTFB). One event per metric per page load,
  // props carry { metric, value, rating, path }. Powers the admin Web Vitals dashboard.
  WEB_VITALS: "web.vitals",
} as const

/**
 * Type for analytics event names
 */
export type AnalyticsEventName =
  (typeof ANALYTICS_EVENT_NAMES)[keyof typeof ANALYTICS_EVENT_NAMES]

/**
 * Organized event constants for easy access (optional convenience)
 */
export const ANALYTICS_EVENTS = {
  MINIGAME: {
    STARTED: ANALYTICS_EVENT_NAMES.MINIGAME_STARTED,
    ATTEMPT: ANALYTICS_EVENT_NAMES.MINIGAME_ATTEMPT,
    COMPLETED: ANALYTICS_EVENT_NAMES.MINIGAME_COMPLETED,
    PLAY_AGAIN: ANALYTICS_EVENT_NAMES.MINIGAME_PLAY_AGAIN,
    VIEW: ANALYTICS_EVENT_NAMES.MINIGAME_VIEW,
  },
  STORY: {
    STARTED: ANALYTICS_EVENT_NAMES.STORY_STARTED,
    COMPLETED: ANALYTICS_EVENT_NAMES.STORY_COMPLETED,
  },
  CODEX: {
    SEARCH_SUBMITTED: ANALYTICS_EVENT_NAMES.CODEX_SEARCH_SUBMITTED,
    SEARCH_VIEWED: ANALYTICS_EVENT_NAMES.CODEX_SEARCH_VIEWED,
  },
  FEEDBACK: {
    SUBMITTED: ANALYTICS_EVENT_NAMES.FEEDBACK_SUBMITTED,
  },
  PAGE: {
    VIEW: ANALYTICS_EVENT_NAMES.PAGE_VIEW,
    PAGEVIEW: ANALYTICS_EVENT_NAMES.PAGEVIEW,
  },
  NAVIGATION: {
    LINK_CLICKED: ANALYTICS_EVENT_NAMES.LINK_CLICKED,
    FORM_SUBMITTED: ANALYTICS_EVENT_NAMES.FORM_SUBMITTED,
  },
  FEATURE: {
    UPSELL_VIEWED: ANALYTICS_EVENT_NAMES.FEATURE_UPSELL_VIEWED,
    NO_ACCESS_VIEWED: ANALYTICS_EVENT_NAMES.FEATURE_NO_ACCESS_VIEWED,
  },
  MODAL: {
    OPENED: ANALYTICS_EVENT_NAMES.MODAL_OPENED,
    SUBMITTED: ANALYTICS_EVENT_NAMES.MODAL_SUBMITTED,
    PAGE_VIEWED: ANALYTICS_EVENT_NAMES.MODAL_PAGE_VIEWED,
  },
} as const
